import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';

const fmtCFA = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' CFA';

const statusConfig = {
    draft:      { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
    sent:       { color: 'bg-blue-100 text-blue-700', label: 'Sent' },
    partial:    { color: 'bg-yellow-100 text-yellow-700', label: 'Partial' },
    received:   { color: 'bg-emerald-100 text-emerald-700', label: 'Received' },
    cancelled:  { color: 'bg-red-100 text-red-700', label: 'Cancelled' },
};

const PurchaseOrders = () => {
    const { authFetch } = useContext(AppContext);
    const { t } = useTranslation();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewPO, setViewPO] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [toast, setToast] = useState(null);

    const [form, setForm] = useState({
        supplier_name: '', expected_date: '', notes: '',
        items: [{ product_id: '', product_name: '', ordered_qty: 1, unit_cost: 0 }]
    });
    const [productSearch, setProductSearch] = useState({});
    const [productResults, setProductResults] = useState({});

    const fetchOrders = () => {
        setLoading(true);
        authFetch('/api/purchase_orders')
            .then(r => r.json())
            .then(d => { setOrders(Array.isArray(d) ? d : []); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(() => { fetchOrders(); }, []);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const openPO = async (po) => {
        const res = await authFetch(`/api/purchase_orders/${po.id}`);
        const data = await res.json();
        setViewPO(data);
    };

    const updateStatus = async (poId, status) => {
        const res = await authFetch(`/api/purchase_orders/${poId}`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
        if (res.ok) {
            showToast(t('Status updated'));
            fetchOrders();
            setViewPO(prev => prev ? { ...prev, status } : null);
        }
    };

    const searchProduct = async (idx, query) => {
        setProductSearch(p => ({ ...p, [idx]: query }));
        if (query.length < 2) { setProductResults(p => ({ ...p, [idx]: [] })); return; }
        const res = await authFetch(`/api/products?search=${encodeURIComponent(query)}`);
        const data = await res.json();
        setProductResults(p => ({ ...p, [idx]: Array.isArray(data) ? data.slice(0, 6) : [] }));
    };

    const selectProduct = (idx, product) => {
        const items = [...form.items];
        items[idx] = { ...items[idx], product_id: product.id, product_name: product.name, unit_cost: parseFloat(product.price || 0) };
        setForm(f => ({ ...f, items }));
        setProductSearch(p => ({ ...p, [idx]: product.name }));
        setProductResults(p => ({ ...p, [idx]: [] }));
    };

    const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', product_name: '', ordered_qty: 1, unit_cost: 0 }] }));
    const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const handleCreate = async (e) => {
        e.preventDefault();
        const res = await authFetch('/api/purchase_orders', {
            method: 'POST',
            body: JSON.stringify(form)
        });
        const data = await res.json();
        if (res.ok) {
            showToast(`${t('PO Created:')} ${data.po_number}`);
            setShowCreate(false);
            setForm({ supplier_name: '', expected_date: '', notes: '', items: [{ product_id: '', product_name: '', ordered_qty: 1, unit_cost: 0 }] });
            fetchOrders();
        } else {
            showToast(data.message || t('Failed'), 'error');
        }
    };

    const columns = [
        { header: t('PO Number'), accessor: 'po_number', render: r => <span className="font-mono font-bold text-indigo-600">{r.po_number}</span> },
        { header: t('Supplier'), accessor: 'supplier_name' },
        { header: t('Created By'), accessor: 'created_by_name' },
        { header: t('Items'), accessor: 'item_count', render: r => <span className="font-bold">{r.item_count}</span> },
        { header: t('Total'), accessor: 'total_amount', render: r => <span className="font-bold text-emerald-600">{fmtCFA(r.total_amount)}</span> },
        { header: t('Status'), accessor: 'status', render: r => {
            const cfg = statusConfig[r.status] || statusConfig.draft;
            return <span className={`px-2 py-1 rounded-full text-xs font-bold ${cfg.color}`}>{t(cfg.label)}</span>;
        }},
        { header: t('Created'), accessor: 'created_at', render: r => new Date(r.created_at).toLocaleDateString() },
        { header: t('Actions'), accessor: 'id', render: r => (
            <button onClick={() => openPO(r)} className="text-indigo-600 hover:text-indigo-800 font-bold text-sm px-3 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition">
                {t('View')}
            </button>
        )}
    ];

    return (
        <div className="space-y-6">
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl font-bold text-white ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`}>
                    {toast.msg}
                </div>
            )}

            <header className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <span className="text-3xl">📋</span> {t('Purchase Orders')}
                    </h2>
                    <p className="text-gray-500 mt-1">{t('Manage and track all supplier purchase orders.')}</p>
                </div>
                <button onClick={() => setShowCreate(true)} className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow transition flex items-center gap-2">
                    ➕ {t('New PO')}
                </button>
            </header>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <DataTable columns={columns} data={orders} />
            </div>

            {/* View PO Modal */}
            <Modal isOpen={!!viewPO} onClose={() => setViewPO(null)} title={`${t('Purchase Order')} — ${viewPO?.po_number}`} size="max-w-2xl">
                {viewPO && (
                    <div className="space-y-4 p-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="bg-gray-50 p-3 rounded-lg"><span className="text-gray-500">{t('Supplier')}: </span><strong>{viewPO.supplier_name}</strong></div>
                            <div className="bg-gray-50 p-3 rounded-lg"><span className="text-gray-500">{t('Created By')}: </span><strong>{viewPO.created_by_name}</strong></div>
                            <div className="bg-gray-50 p-3 rounded-lg"><span className="text-gray-500">{t('Status')}: </span>
                                <span className={`font-bold px-2 py-0.5 rounded-full text-xs ${statusConfig[viewPO.status]?.color}`}>{t(statusConfig[viewPO.status]?.label)}</span>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg"><span className="text-gray-500">{t('Total')}: </span><strong className="text-emerald-600">{fmtCFA(viewPO.total_amount)}</strong></div>
                        </div>

                        <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-sm border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    {[t('Product'), t('Ordered'), t('Received'), t('Unit Cost'), t('Total')].map(h => (
                                        <th key={h} className="p-3 text-left font-bold text-gray-600">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(viewPO.items || []).map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="p-3 font-medium">{item.product_name}</td>
                                        <td className="p-3 font-bold">{parseFloat(item.ordered_qty)}</td>
                                        <td className="p-3">{parseFloat(item.received_qty)}</td>
                                        <td className="p-3">{fmtCFA(item.unit_cost)}</td>
                                        <td className="p-3 font-bold text-emerald-600">{fmtCFA(item.ordered_qty * item.unit_cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
</div>

                        {viewPO.status !== 'received' && viewPO.status !== 'cancelled' && (
                            <div className="flex gap-3 justify-end border-t pt-4">
                                {viewPO.status === 'draft' && (
                                    <button onClick={() => updateStatus(viewPO.id, 'sent')} className="px-4 py-2 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600">
                                        📤 {t('Mark as Sent')}
                                    </button>
                                )}
                                <button onClick={() => updateStatus(viewPO.id, 'received')} className="px-4 py-2 bg-emerald-500 text-white rounded-lg font-bold hover:bg-emerald-600">
                                    ✅ {t('Mark as Received & Update Stock')}
                                </button>
                                <button onClick={() => updateStatus(viewPO.id, 'cancelled')} className="px-4 py-2 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600">
                                    ❌ {t('Cancel')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Create PO Modal */}
            <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title={t('Create Purchase Order')} size="max-w-2xl">
                <form onSubmit={handleCreate} className="space-y-4 p-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">{t('Supplier Name')}</label>
                            <input type="text" value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))}
                                className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1">{t('Expected Delivery Date')}</label>
                            <input type="date" value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))}
                                className="w-full border p-2.5 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">{t('Items')}</label>
                        {form.items.map((item, idx) => (
                            <div key={idx} className="flex gap-2 items-start">
                                <div className="flex-1 relative">
                                    <input type="text" value={productSearch[idx] || item.product_name}
                                        onChange={e => searchProduct(idx, e.target.value)}
                                        placeholder={t('Search product...')}
                                        className="w-full border p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400" />
                                    {(productResults[idx] || []).length > 0 && (
                                        <div className="absolute z-10 top-full left-0 right-0 bg-white border border-gray-200 rounded-b-lg shadow-lg max-h-48 overflow-y-auto">
                                            {productResults[idx].map(p => (
                                                <div key={p.id} onClick={() => selectProduct(idx, p)}
                                                    className="p-2 hover:bg-indigo-50 cursor-pointer text-sm">
                                                    <span className="font-medium">{p.name}</span>
                                                    <span className="text-gray-400 ml-2">{p.sku}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <input type="number" min="1" value={item.ordered_qty}
                                    onChange={e => { const i = [...form.items]; i[idx].ordered_qty = e.target.value; setForm(f => ({ ...f, items: i })); }}
                                    placeholder={t('Qty')} className="w-20 border p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400" />
                                <input type="number" min="0" value={item.unit_cost}
                                    onChange={e => { const i = [...form.items]; i[idx].unit_cost = e.target.value; setForm(f => ({ ...f, items: i })); }}
                                    placeholder={t('Cost')} className="w-28 border p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400" />
                                {form.items.length > 1 && (
                                    <button type="button" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 p-2">🗑️</button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={addItem} className="text-indigo-600 font-bold text-sm hover:underline">+ {t('Add Item')}</button>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">{t('Notes')}</label>
                        <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                            className="w-full border p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" rows="2" />
                    </div>

                    <div className="flex justify-end gap-3 border-t pt-4">
                        <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200">{t('Cancel')}</button>
                        <button type="submit" className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 shadow">{t('Create Purchase Order')}</button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default PurchaseOrders;
